// Shared name-match scoring used by the entity/ownership results table and by
// the Beneficial Ownership agent call. Keeping a single source of truth ensures
// the score shown in the table is the same one used to decide which records get
// sent to the agent.

const normalizeName = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Jaro-Winkler similarity (0..1). Good for names — rewards shared characters
// in order and gives a small boost when the strings share a prefix.
const jaroWinkler = (a: string, b: string): number => {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  const matchWindow = Math.max(
    0,
    Math.floor(Math.max(a.length, b.length) / 2) - 1,
  );
  const aMatches = new Array(a.length).fill(false);
  const bMatches = new Array(b.length).fill(false);
  let matches = 0;
  for (let i = 0; i < a.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, b.length);
    for (let j = start; j < end; j++) {
      if (bMatches[j] || a[i] !== b[j]) continue;
      aMatches[i] = true;
      bMatches[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;
  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aMatches[i]) continue;
    while (!bMatches[k]) k++;
    if (a[i] !== b[k]) transpositions++;
    k++;
  }
  transpositions /= 2;
  const jaro =
    (matches / a.length +
      matches / b.length +
      (matches - transpositions) / matches) /
    3;
  let prefix = 0;
  const maxPrefix = Math.min(4, a.length, b.length);
  for (let i = 0; i < maxPrefix; i++) {
    if (a[i] === b[i]) prefix++;
    else break;
  }
  return jaro + prefix * 0.1 * (1 - jaro);
};

// Best name-match confidence (0..100) between the searched term and any of the
// candidate names. Blends a character-level score (Jaro-Winkler) with token
// overlap so "Acme" vs "Acme Corp LLC" still scores high. Returns null when
// there is no search term to compare against.
const nameSimilarity = (
  term: string,
  candidates: string[],
): number | null => {
  const a = normalizeName(term);
  if (!a) return null;
  let best = 0;
  candidates.forEach((candidate) => {
    const b = normalizeName(candidate);
    if (!b) return;
    if (a === b) {
      best = 1;
      return;
    }
    const jw = jaroWinkler(a, b);
    const ta = new Set(a.split(' ').filter(Boolean));
    const tb = new Set(b.split(' ').filter(Boolean));
    const inter = [ ...ta ].filter((t) => tb.has(t)).length;
    const union = new Set([ ...ta, ...tb ]).size;
    const jaccard = union ? inter / union : 0;
    const containment = Math.min(ta.size, tb.size)
      ? inter / Math.min(ta.size, tb.size)
      : 0;
    best = Math.max(best, jw, jaccard, containment * 0.92);
  });
  return Math.round(Math.min(1, best) * 100);
};

const splitFieldValues = (raw: any): string[] =>
  Array.isArray(raw)
    ? raw.map((v: any) => String(v))
    : String(raw || '').split(/[;|\n]/);

// Fields the match score compares the searched name against. Beyond the primary
// name and aliases, this includes Related Entity, Name of Interested Party, and
// Additional Remarks so a name that only appears in those fields still counts.
const CANDIDATE_NAME_FIELDS = [
  'Name of Entity',
  'Alternative Names',
  'Related Entity',
  'Name of Interested Party',
  'Additional Remarks',
];

const CANONICAL_NAME_FIELD = 'Name of Entity';
const SECONDARY_NAME_FIELDS = CANDIDATE_NAME_FIELDS.filter(
  (field) => field !== CANONICAL_NAME_FIELD,
);

const getCanonicalNames = (source: Record<string, any>): string[] =>
  splitFieldValues(source?.[CANONICAL_NAME_FIELD]).filter(
    (value) => value && value.trim() !== '',
  );

const getSecondaryNames = (source: Record<string, any>): string[] => {
  const values: string[] = [];
  SECONDARY_NAME_FIELDS.forEach((field) => {
    splitFieldValues(source?.[field]).forEach((value) => {
      if (value && value.trim() !== '') {
        values.push(value);
      }
    });
  });
  return values;
};

// A secondary field (Alternative Names, Related Entity, etc.) matching the
// search term exactly can otherwise drive the score to 100% even when the
// canonical "Name of Entity" is a weak match to what was searched (e.g.
// searching an abbreviation like "B&A" that only appears verbatim in
// Alternative Names). Weight the canonical name as the primary signal, and
// let a secondary field only lift the score by a fraction of how far above
// the canonical score it lands -- so it can push a decent canonical match a
// bit higher, but can't alone turn a weak canonical match into a "high
// confidence" (>=90, see getScoreBand callers) one.
const SECONDARY_NAME_LIFT_WEIGHT = 0.3;

export const getEntityMatchScore = (
  term: string,
  source: Record<string, any>,
): number | null => {
  const canonicalNames = getCanonicalNames(source);
  if (canonicalNames.length === 0) {
    // No canonical name to weight against at all -- e.g. a record whose name
    // only lives in Related Entity / Additional Remarks. There is no "weak
    // primary" being masked here, so don't penalize; use the secondary
    // fields' own score as-is (matches pre-existing behavior for these rows).
    return nameSimilarity(term, getSecondaryNames(source));
  }
  const primary = nameSimilarity(term, canonicalNames);
  if (primary === null) return null;
  const secondary = nameSimilarity(term, getSecondaryNames(source)) ?? 0;
  const blended =
    secondary > primary
      ? primary + (secondary - primary) * SECONDARY_NAME_LIFT_WEIGHT
      : primary;
  return Math.round(Math.min(100, blended));
};
